import Cookies from 'js-cookie';
import axios from '../utils/axiosClient';
import endpoints from './endpoints';
import { CodeVerifyOptions, InitTOTPResDto, TotpAction } from '@/interfaces/totp';
import { ActionType } from '../utils/constants';

const {
  validatePhone,
  verifyOtp,
  createMfaChallenge,
  verifyMfaChallenge,
  resendMfaChallenge,
  createAuthChallenge,
  verifyChallenge,
  resendChallenge,
  otpResend,
  forgotPass,
  getAccounts,
  change2Fa,
  getMe,
  changeUserPassword,
  otpPayment,
  changeUserLanguage,
  printAccountReport,
  getExchangeRates,
  getSubUsers,
  getSubUserTransaction,
  getSesions,
  getCbsState,
  updateMFA,
  updateAutoRenewSub,
  getAlerts,
  readAlert,
  readAll,
  userSettings,
  initTOTP,
  finalizeTOTOSetup,
  getUnusedBackupCodes,
  regenerateBackupCodes,
  updateTotpStatus,
  getTotpStatus,
  deleteTotpDevice,
} = endpoints;

const phoneValidate = async (phoneNumber: string) => {
  const data = { phone: phoneNumber };

  try {
    const response = await axios.post(validatePhone, data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const codeVerify = async (code: string, forgotFlow: boolean = false, options?: CodeVerifyOptions) => {
  const data = {
    code,
    actionType: options?.actionType,
    tokenType: options?.tokenType,
    isBackupCode: options?.isBackupCode,
  };

  try {
    const url = forgotFlow ? `${verifyOtp}?actionType=${ActionType.UPDATE_PASSWORD}` : verifyOtp;
    const response = await axios.post(url, data);
    Cookies.remove('tmpToken');
    Cookies.set('accessToken', response.data.accessToken, { expires: 1 / 24, path: '/' });
    return response;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const resendChallengeCode = async (challengeId: string) => {
  try {
    const response = await axios.post(resendChallenge(challengeId));
    return response;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const createAuthChallengeCode = async (username: string, policyKey: string, channel: string = 'SMS') => {
  try {
    const response = await axios.post(createAuthChallenge, { username, policyKey, channel });
    Cookies.set('tmpToken', response.data.accessToken, { expires: 1 });
    return response;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const createMfaChallengeCode = async (policyKey: string, channel: string = 'SMS', actionRef: string = '') => {
  try {
    const response = await axios.post(createMfaChallenge, {
      policyKey,
      channel,
      actionRef,
    });
    return response;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const resendMfaChallengeCode = async (challengeId: string) => {
  try {
    const response = await axios.post(resendMfaChallenge(challengeId));
    return response;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const verifyMfaChallengeCode = async (challengeId: string, code: string, policyKey: string, isBackupCode: boolean = false) => {
  try {
    const response = await axios.post(verifyMfaChallenge(challengeId), {
      code,
      policyKey,
      isBackupCode,
    });
    return response;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const verifyChallengeCode = async (challengeId: string, code: string, policyKey: string = 'LOGIN', isBackupCode: boolean = false) => {
  try {
    const response = await axios.post(verifyChallenge(challengeId), {
      code,
      policyKey,
      isBackupCode,
    });
    Cookies.remove('tmpToken');
    Cookies.set('accessToken', response.data.accessToken, { expires: 1 / 24, path: '/' });
    return response;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const resendOtp = async (forgotFlow: boolean = false) => {
  try {
    const url = forgotFlow ? `${otpResend}?actionType=UPDATE_PASSWORD` : otpResend;
    const response = await axios.post(url);
    return response;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const paymentOtp = async (uuid: string, actionType: string) => {
  const data = {
    actionId: uuid,
    actionType: actionType,
  };

  try {
    const response = await axios.post(otpPayment, data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const forgotPassword = async (username: string) => {
  try {
    const response = await axios.post(`${forgotPass}?actionType=UPDATE_PASSWORD`, { username });
    Cookies.set('tmpToken', response.data.accessToken, { expires: 1 });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const accounts = async () => {
  try {
    const response = await axios.get(getAccounts);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const account = async (id: string) => {
  try {
    const response = await axios.get(`${getAccounts}/${id}`);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const printAccountDetails = async (id: string, options: { displayBalance: boolean; displaySubUsers: boolean }) => {
  try {
    const response = await axios.get(`${printAccountReport}/${id}`, { params: options, responseType: 'blob' });
    const fileBlob = new Blob([response.data], { type: response.headers['content-type'] });
    const fileUrl = URL.createObjectURL(fileBlob);

    return window.open(fileUrl, '_blank');
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const accountByNumber = async (accNumber: string) => {
  try {
    const response = await axios.get(`${getAccounts}?accountNumber=${accNumber}`);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const update2Fa = async (mfaEnabled: boolean, mfaType: string | undefined, password: string | undefined) => {
  const data = { mfaEnabled, mfaType, password };
  try {
    const response = await axios.post(change2Fa, data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const loadSubUsers = async () => {
  try {
    const response = await axios.get(`${getSubUsers}`);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const loadSubUserTrans = async (params: any) => {
  try {
    const response = await axios.get(getSubUserTransaction, { params });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const meFallbackOnBillNotFound = () => ({
  id: 'bill_not_found',
  firstName: '',
  lastName: '',
  username: '',
  phone: '',
  email: '',
  permissions: [],
  subscriptionEndDate: null,
  duePaymentDate: null,
  isSubUser: false,
  isMain: false,
  mfaEnabled: false,
  mfaType: undefined,
  isFirstPasswordChanged: false,
  subscriptionInfo: { billingAccountNumber: '', isAutoCharging: false },
  packageInfo: { description: '', title: '', freeOfCharge: false, maxNumberSubUsers: 0, periodicity: '', permissions: [], pricing: [] },
  validatorsCount: 0,
  unreadAlertsCount: 0,
  permanentRole: '',
  billingError: 'bill_not_found',
});

const me = async () => {
  try {
    const response = await axios.get(getMe);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.code === 'bill_not_found') {
      return meFallbackOnBillNotFound();
    }
    return Promise.reject(error.response);
  }
};

const updateAccountName = async (id: string, data: any) => {
  try {
    const response = await axios.put(`${getAccounts}/${id}/name`, data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const updateAccountDisplaying = async (data: any) => {
  try {
    const response = await axios.put(getAccounts, data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const updateUserPassword = async (data: any) => {
  try {
    const response = await axios.post(changeUserPassword, data);
    if (response.data.accessToken) {
      Cookies.set('accessToken', response.data.accessToken, { expires: 1 / 24, path: '/' });
    }
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const getExchRates = async () => {
  try {
    const response = await axios.get(getExchangeRates);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const getSessions = async () => {
  try {
    const response = await axios.get(getSesions);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const getCBSStatus = async () => {
  try {
    const response = await axios.get(getCbsState);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const updateMfa = async (data: any) => {
  try {
    const response = await axios.post(updateMFA, data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const updateAutoRenew = async (data: any) => {
  try {
    const response = await axios.put(updateAutoRenewSub, data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const updateUserLanguage = async (language: string) => {
  try {
    const response = await axios.post(changeUserLanguage, { language });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const fetchAlerts = async (params: any) => {
  try {
    const response = await axios.get(getAlerts, { params });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const readNotification = async (alertId: string) => {
  try {
    const response = await axios.put(readAlert(alertId));
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};
const readAllNotifications = async () => {
  try {
    const response = await axios.put(readAll, {});
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const getUserSettings = async () => {
  try {
    const response = await axios.get(userSettings);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const setUserSettings = async (data: any) => {
  try {
    const response = await axios.post(userSettings, data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const initialiseTOTP = async (password: string) => {
  try {
    const response = await axios.post<InitTOTPResDto>(initTOTP, { password });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const finalizeTOTPSetup = async (code: string) => {
  try {
    const response = await axios.post(finalizeTOTOSetup, { code });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const getBackupCodesCount = async () => {
  try {
    const response = await axios.get(getUnusedBackupCodes);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const renewBackupCodes = async (password: string) => {
  try {
    const response = await axios.post(regenerateBackupCodes, { password });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const updateTOTPStatus = async (password: string, action: TotpAction) => {
  try {
    const response = await axios.post(updateTotpStatus, { password, action });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const getTOTPStatus = async () => {
  try {
    const response = await axios.get(getTotpStatus);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const deleteTOTPDevice = async (password: string) => {
  try {
    const response = await axios.delete(deleteTotpDevice, {
      data: { password },
    });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const userServices = {
  phoneValidate,
  codeVerify,
  createAuthChallengeCode,
  createMfaChallengeCode,
  resendMfaChallengeCode,
  verifyMfaChallengeCode,
  verifyChallengeCode,
  resendChallengeCode,
  resendOtp,
  forgotPassword,
  accounts,
  account,
  update2Fa,
  updateMfa,
  me,
  updateAccountName,
  accountByNumber,
  updateUserPassword,
  updateAccountDisplaying,
  paymentOtp,
  printAccountDetails,
  getExchRates,
  getSessions,
  loadSubUsers,
  loadSubUserTrans,
  getCBSStatus,
  updateAutoRenew,
  updateUserLanguage,
  readNotification,
  fetchAlerts,
  readAllNotifications,
  getUserSettings,
  setUserSettings,
  initialiseTOTP,
  finalizeTOTPSetup,
  getBackupCodesCount,
  renewBackupCodes,
  updateTOTPStatus,
  getTOTPStatus,
  deleteTOTPDevice,
};

export default userServices;
